// spec 260509-system-monitor B-3：共享 SVG sparkline。
//
// 用户反馈 (2026-05-09):
//   - 旧版 viewBox + preserveAspectRatio="none" 横向拉伸，让 Y 轴文字 "100%"
//     被拉得变形 / 被父卡片右 padding 截断.
//   - 全屏后 sparkline 不会随容器变大 (固定 width/height props).
//   - 折线太扁、没有纵坐标.
//
// 改造思路:
//   - 用 ResizeObserver 测真实容器尺寸, SVG 直接画到真实像素 (不再走 viewBox
//     拉伸), 文字保持正常字形, 不被截断.
//   - 默认 ``width: 100%``, 高度由父 className (CSS height) 决定.
//   - Y 轴标签放在折线**左侧**, 不会被父 overflow / 圆角覆盖.
//   - 新增 ``yAxis`` prop ('percent' | 'auto' | null): percent → 0/50/100;
//     auto → min/mid/max; null → 不画 (老 widget 迷你 sparkline 用).

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';

export type SparkPoint = number | null;

export type SparkYAxis = 'percent' | 'auto' | null;

export interface SparklineProps {
  /** 点数组, 按时间顺序最旧 → 最新; ``null`` 代表数据缺失 (SVG 起笔重置). */
  points: SparkPoint[];
  /** 警戒线 (数值), 非空时画虚线 + 末值超阈值则整图染 warn 色. */
  warnAbove?: number | null;
  /** 主色; 默认 ``currentColor``, 让父组件用 CSS class 控色. */
  color?: string;
  /** ARIA label, 默认"sparkline". */
  ariaLabel?: string;
  /** 透传 className, 便于父组件加交互 / 状态样式 + 控真实宽高. */
  className?: string;
  style?: CSSProperties;
  /**
   * 纵坐标参考线策略:
   *   - ``'percent'``: 0 / 50 / 100 三条虚线 + 左侧 tick 标签.
   *   - ``'auto'``: 按数据自动 3 等分 (min / mid / max).
   *   - ``null`` (默认): 不画 (widget 迷你 sparkline 用).
   */
  yAxis?: SparkYAxis;
  /** Y 轴文字字号 (像素); 默认 11. */
  yAxisFontSize?: number;
  /**
   * Y 轴 tick 格式化函数 (像素值 → 显示字符串). 不传时:
   *   - ``yAxis === 'percent'`` → ``"100%"``
   *   - ``yAxis === 'auto'``    → ``Math.round(v).toString()``
   * Network / Disk 等大数值场景应传 ``formatBytes`` / ``formatRate`` 这种短格式
   * (e.g. "1.2 MB/s") 避免标签宽度爆掉.
   */
  yAxisFormatter?: (v: number) => string;
  /**
   * Y 轴标签宽度 (像素). 默认 36; 网络 / 磁盘等需要更宽 (5-7 字符如
   * "1.2 MB/s") 的场景传 64 或 72.
   */
  yAxisLabelWidth?: number;
  /**
   * 兼容旧调用: ``width / height``. 仅在 ResizeObserver 还没拿到真实尺寸时
   * 作为首帧 fallback 用; 拿到真实尺寸后忽略.
   */
  width?: number;
  height?: number;
}

const FALLBACK_W = 200;
const FALLBACK_H = 24;
// Y 轴标签默认宽度 (像素): "100%" 大约 28px. 给 36 留富余, 避免与折线重叠.
// Network / Disk 等大数值场景由调用方传 ``yAxisLabelWidth`` 覆盖.
const Y_AXIS_PAD_LEFT_PX_DEFAULT = 36;
// 上下边距 (像素), 让折线不贴顶 / 贴底.
const PAD_Y_PX = 4;

export function Sparkline({
  points,
  warnAbove = null,
  color = 'currentColor',
  ariaLabel = 'sparkline',
  className,
  style,
  yAxis = null,
  yAxisFontSize = 11,
  yAxisFormatter,
  yAxisLabelWidth = Y_AXIS_PAD_LEFT_PX_DEFAULT,
  width: fallbackW = FALLBACK_W,
  height: fallbackH = FALLBACK_H,
}: SparklineProps): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({
    w: fallbackW,
    h: fallbackH,
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        const nw = Math.max(1, Math.round(cr.width));
        const nh = Math.max(1, Math.round(cr.height));
        setSize((prev) =>
          prev.w === nw && prev.h === nh ? prev : { w: nw, h: nh },
        );
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const w = size.w;
  const h = size.h;
  const lineLeft = yAxis !== null ? yAxisLabelWidth : 0;
  const lineRight = w;
  const lineWidth = Math.max(1, lineRight - lineLeft);

  const vals = points.filter((v): v is number => v !== null);

  // 数据范围 + Y 轴刻度
  let yMin: number;
  let yMax: number;
  let yTicks: number[];
  if (yAxis === 'percent') {
    yMin = 0;
    yMax = 100;
    yTicks = [0, 50, 100];
  } else if (yAxis === 'auto' && vals.length > 0) {
    const dataMax = Math.max(...vals, warnAbove ?? -Infinity);
    const dataMin = Math.min(...vals, 0);
    yMin = dataMin;
    yMax = dataMax || 1;
    yTicks = [yMin, (yMin + yMax) / 2, yMax];
  } else {
    if (vals.length > 0) {
      yMax = Math.max(...vals, warnAbove ?? -Infinity);
      yMin = Math.min(...vals, 0);
    } else {
      yMax = 1;
      yMin = 0;
    }
    yTicks = [];
  }
  const yRange = yMax - yMin || 1;
  const toY = (v: number): number =>
    h - PAD_Y_PX - ((v - yMin) / yRange) * (h - PAD_Y_PX * 2);

  let d = '';
  let pen = false;
  if (points.length > 0) {
    const xStep = points.length > 1 ? lineWidth / (points.length - 1) : 0;
    points.forEach((v, i) => {
      if (v === null) {
        pen = false;
        return;
      }
      const x = lineLeft + i * xStep;
      const y = toY(v);
      d += `${pen ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)} `;
      pen = true;
    });
  }

  // 末值超阈值 → 整图染 warn 色.
  const lastVal = lastNonNull(points);
  const overWarn = warnAbove !== null && lastVal !== null && lastVal > warnAbove;
  const warnClass = overWarn && className ? `${className}-warn` : null;
  const cls = [className, warnClass].filter(Boolean).join(' ') || undefined;

  const fmtTick = (v: number): string => {
    if (yAxisFormatter) return yAxisFormatter(v);
    if (yAxis === 'percent') return `${Math.round(v)}%`;
    return Math.round(v).toString();
  };

  // 空数据 → 占位虚线.
  const isEmpty = vals.length === 0;

  // className 同时挂到 div 上, 让 ``.dash-panel-spark`` 等 CSS height 规则
  // 作用到外层容器 → ResizeObserver 拿到尺寸 → 内层 svg 渲染对应像素.
  // svg 上也保留同名 className, 便于按选择器命中颜色 / warn 状态等.
  return (
    <div
      ref={containerRef}
      className={cls}
      style={{
        width: '100%',
        display: 'block',
        ...style,
      }}
    >
      <svg
        width={w}
        height={h}
        role="img"
        aria-label={isEmpty ? '无数据' : ariaLabel}
        className={cls}
        style={{ display: 'block' }}
      >
        {/* Y 轴参考线 + 标签 (左侧). preserveAspectRatio 不再依赖, 文字按
            像素绘制不会变形. */}
        {yAxis !== null &&
          yTicks.map((tv, i) => {
            const y = toY(tv);
            const isEdge = i === 0 || i === yTicks.length - 1;
            return (
              <g key={i}>
                <line
                  x1={lineLeft}
                  x2={lineRight}
                  y1={y}
                  y2={y}
                  stroke={color}
                  strokeOpacity={isEdge ? 0.18 : 0.1}
                  strokeDasharray="2 3"
                />
                <text
                  x={lineLeft - 4}
                  y={y + yAxisFontSize / 3}
                  fontSize={yAxisFontSize}
                  fill={color}
                  fillOpacity={0.55}
                  textAnchor="end"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {fmtTick(tv)}
                </text>
              </g>
            );
          })}
        {warnAbove !== null && yAxis === null && (
          <line
            x1={lineLeft}
            x2={lineRight}
            y1={toY(warnAbove)}
            y2={toY(warnAbove)}
            stroke={color}
            strokeOpacity={0.15}
            strokeDasharray="2 2"
          />
        )}
        {isEmpty ? (
          <line
            x1={lineLeft}
            x2={lineRight}
            y1={h / 2}
            y2={h / 2}
            stroke={color}
            strokeOpacity={0.2}
            strokeDasharray="3 3"
          />
        ) : (
          <path d={d.trim()} fill="none" stroke={color} strokeWidth={1.4} />
        )}
      </svg>
    </div>
  );
}

function lastNonNull(points: SparkPoint[]): number | null {
  for (let i = points.length - 1; i >= 0; i--) {
    const v = points[i];
    if (v !== null && v !== undefined) return v;
  }
  return null;
}

export default Sparkline;
